import React from 'react';
import { motion } from 'framer-motion';
import { FaDna, FaProjectDiagram, FaUsers, FaGraduationCap } from 'react-icons/fa';
import { Footer } from '../../components/shared/Footer';

export const WhatIsTransmediaIP = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto px-6 py-16"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            What is a Transmedia IP Development Platform?
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            The foundational technology category that enables creators to build, manage, and express intellectual property across infinite media formats through structured human-AI collaboration.
          </p>
        </motion.div>

        {/* Core Definition */}
        <motion.section variants={itemVariants} className="mb-16">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-blue-400">The Definitive Definition</h2>
            <div className="text-lg leading-relaxed space-y-4">
              <p className="border-l-4 border-purple-400 pl-6 italic text-gray-200">
                "A <strong>Transmedia IP Development Platform</strong> is a comprehensive software ecosystem that empowers creators to build, manage, and express intellectual property across multiple media formats from a single source of truth, treating narrative as foundational DNA while maintaining human creative control through intelligent AI assistance."
              </p>
              <p className="text-gray-300">
                Unlike traditional content creation tools that focus on individual outputs, or AI tools that attempt to replace human creativity, a transmedia IP platform recognizes that the most valuable element is the <strong>structured creative partnership</strong> between human imagination and AI capability, resulting in consistent, scalable intellectual property.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Human-AI Creative Partnership */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-green-400">Human-AI Creative Partnership</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaUsers className="text-3xl text-green-400 mr-4" />
                <h3 className="text-xl font-bold">Human-Driven Creativity</h3>
              </div>
              <p className="text-gray-300">
                Creators maintain complete creative control, making all decisions about characters, worlds, plots, and tone. The platform requires active human engagement through structured brainstorming, character development, and story architecture.
              </p>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaGraduationCap className="text-3xl text-blue-400 mr-4" />
                <h3 className="text-xl font-bold">AI as Creative Tutor</h3>
              </div>
              <p className="text-gray-300">
                AI serves as an intelligent assistant that teaches storytelling concepts, suggests improvements, maintains consistency, and handles technical formatting—amplifying human creativity rather than replacing it.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-xl p-6">
            <h4 className="text-lg font-bold mb-3 text-green-300">The Structured Creative Process</h4>
            <p className="text-gray-300 mb-4">
              Transmedia IP platforms guide creators through proven storytelling frameworks: <strong>Brainstorming → Character Development → World Building → Story Structure → Story Beats → Scene Cards</strong>. This structure ensures that by the time creators are ready to write, they have a comprehensive foundation that can support any medium.
            </p>
            <p className="text-gray-300">
              <strong>From Blank Page to Story:</strong> The platform solves the "blank page problem" by providing structure without constraining creativity, turning scattered ideas into coherent, expandable intellectual property.
            </p>
          </div>
        </motion.section>

        {/* The Narrative DNA Philosophy */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-purple-400">The Narrative DNA Philosophy</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaDna className="text-3xl text-blue-400 mr-4" />
                <h3 className="text-xl font-bold">Story as Source Code</h3>
              </div>
              <p className="text-gray-300">
                Your intellectual property's narrative elements—characters, worlds, relationships, and conflicts—function as the DNA from which all content expressions emerge. This foundational layer ensures consistency while allowing infinite creative expression.
              </p>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <FaProjectDiagram className="text-3xl text-purple-400 mr-4" />
                <h3 className="text-xl font-bold">Infinite Expression</h3>
              </div>
              <p className="text-gray-300">
                From this single source of truth, your narrative DNA can be expressed through novels, screenplays, games, graphic novels, podcasts, and any future medium—each maintaining the essential story while optimizing for its specific format.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6">
            <h4 className="text-lg font-bold mb-3 text-blue-300">Beyond Format Adaptation</h4>
            <p className="text-gray-300">
              Traditional adaptation treats each format as separate, often losing essential elements in translation. Transmedia IP Development Platforms maintain the essential DNA while allowing each medium to express that DNA naturally—a screenplay structured like a screenplay, a game designed like a game, but all sharing the same foundational truth and character consistency.
            </p>
          </div>
        </motion.section>

        {/* Historical Context */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-orange-400">Why This Category Emerges Now</h2>
          
          <div className="space-y-6">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-orange-300">The Creator Economy Problem</h3>
              <p className="text-gray-300 mb-4">
                Individual creators want to build universe-scale intellectual properties but lack the tools and knowledge traditionally available only to major studios. Most have great ideas for characters and worlds but struggle to structure them into coherent, expandable stories.
              </p>
              <p className="text-gray-300">
                Transmedia IP Development Platforms democratize access to professional storytelling frameworks while maintaining the creative vision and control that independent creators value.
              </p>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-orange-300">Technology Enablers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-orange-200 mb-2">AI Capabilities</h4>
                  <ul className="space-y-1 text-gray-300 text-sm">
                    <li>• Context-aware creative assistance</li>
                    <li>• Semantic understanding of narrative</li>
                    <li>• Intelligent tutoring systems</li>
                    <li>• Cross-format content generation</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-200 mb-2">Market Demand</h4>
                  <ul className="space-y-1 text-gray-300 text-sm">
                    <li>• Multi-platform content requirements</li>
                    <li>• IP-driven entertainment value</li>
                    <li>• Community-driven storytelling</li>
                    <li>• Creator economic independence</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Key Differentiators */}
        <motion.section variants={itemVariants} className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-red-400">Beyond Traditional Tools</h2>
          
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <h3 className="text-lg font-bold mb-3 text-red-300">Traditional Writing Software</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>Single format output</li>
                  <li>Manual consistency checking</li>
                  <li>No structural guidance</li>
                  <li>Assumes storytelling knowledge</li>
                </ul>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-bold mb-3 text-yellow-300">AI Writing Tools</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>"Prompt and pray" approach</li>
                  <li>No narrative structure</li>
                  <li>Inconsistent outputs</li>
                  <li>Replaces human creativity</li>
                </ul>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-bold mb-3 text-green-300">Transmedia IP Platforms</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>Human-AI creative partnership</li>
                  <li>Structured storytelling education</li>
                  <li>Multi-format consistency</li>
                  <li>Context-aware workflows</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section variants={itemVariants} className="text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-8">
            <h2 className="text-2xl font-bold mb-4">Ready to Build Your Universe?</h2>
            <p className="text-gray-300 mb-6">
              Omnigeist is pioneering this category with Story Forge—the first tool that teaches you storytelling while you build your intellectual property. Transform your scattered ideas into structured, expandable universes through guided human-AI collaboration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact?type=product"
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold text-white hover:from-blue-600 hover:to-purple-600 transition-all inline-block"
              >
                Join Early Access
              </a>
            </div>
          </div>
        </motion.section>
      </motion.div>
      <Footer />
    </div>
  );
};

export default WhatIsTransmediaIP; 